import clsx from 'clsx'
import { ChevronDown, MessageCircle, Search, ShieldAlert, ShoppingBag, Users } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import type { CrmClient, CrmResponse, CrmSearchType } from '@/types'
import { EmptyState, ErrorNote, LoadingScreen, Spinner } from '@/components/ui'
import { api } from '@/lib/api'
import { currency, shortDate, timeAgoFromMinutes } from '@/lib/format'
import { useAsync } from '@/lib/useAsync'

const TYPES: { key: CrmSearchType; label: string }[] = [
  { key: 'name', label: 'Nombre' },
  { key: 'dni', label: 'DNI' },
  { key: 'email', label: 'Email' },
]

function PrivacyBanner() {
  return (
    <div className="flex items-start gap-2 border-b border-warning/30 bg-warning/10 px-4 py-3 text-[12px] leading-relaxed text-ink-soft">
      <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
      <p>
        <strong>Información confidencial.</strong> Estos datos son propiedad de la empresa y exclusivos
        para la gestión de ventas oficial. Queda prohibida su captura, almacenamiento o divulgación fuera
        de la plataforma. Toda infracción será reportada al departamento legal.
      </p>
    </div>
  )
}

function ClientRow({ client }: { client: CrmClient }) {
  const [open, setOpen] = useState(false)
  const [detail, setDetail] = useState<CrmClient | null>(null)
  const [loading, setLoading] = useState(false)

  async function toggle() {
    const next = !open
    setOpen(next)
    if (next && !detail) {
      setLoading(true)
      try {
        setDetail(await api.get<CrmClient>(`/crm/detail?id=${encodeURIComponent(client.id)}`))
      } catch {
        setDetail(client) // al menos mostrar lo que ya tenemos
      } finally {
        setLoading(false)
      }
    }
  }

  const data = detail ?? client

  return (
    <div className="card overflow-hidden">
      <button className="flex w-full items-center gap-3 p-3 text-left" onClick={toggle}>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-ink">{client.maskedName}</p>
          <p className="truncate text-xs text-ink-muted">{client.maskedEmail}</p>
        </div>
        {client.hasAbandonedCart && (
          <span className="rounded-md bg-warning/15 px-2 py-0.5 text-[10px] font-semibold text-warning">
            Carrito abandonado
          </span>
        )}
        <ChevronDown className={clsx('h-4 w-4 text-ink-faint transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="border-t border-line px-3 py-3">
          {loading ? (
            <div className="flex justify-center py-4">
              <Spinner className="h-5 w-5 accent-text" />
            </div>
          ) : (
            <div className="space-y-3">
              {data.maskedPhone && (
                <p className="text-xs text-ink-muted">Tel: {data.maskedPhone}</p>
              )}
              {data.maskedDocument && (
                <p className="text-xs text-ink-muted">DNI: {data.maskedDocument}</p>
              )}

              {data.abandonedCart && (
                <div className="rounded-lg bg-warning/10 p-3">
                  <p className="text-xs font-semibold text-ink">Carrito abandonado</p>
                  <p className="text-xs text-ink-muted">
                    {data.abandonedCart.productName} · {timeAgoFromMinutes(data.abandonedCart.minutesAgo)}
                  </p>
                </div>
              )}

              <div>
                <p className="mb-1 text-xs font-semibold text-ink-soft">Últimas compras</p>
                {data.purchases.length === 0 ? (
                  <p className="text-xs text-ink-faint">Sin compras registradas.</p>
                ) : (
                  <ul className="space-y-1">
                    {data.purchases.map((p, i) => (
                      <li key={i} className="flex items-center justify-between text-xs">
                        <span className="min-w-0 flex-1 truncate text-ink">
                          {shortDate(p.date)} · {p.productName}
                        </span>
                        <span className="font-semibold text-ink">{currency(p.amount)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {data.hasAbandonedCart && data.whatsappUrl && (
                <a href={data.whatsappUrl} target="_blank" rel="noreferrer" className="btn-primary btn-block">
                  <MessageCircle className="h-4 w-4" />
                  Recordar por WhatsApp
                </a>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export function Crm() {
  const [type, setType] = useState<CrmSearchType>('name')
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 350)
    return () => clearTimeout(t)
  }, [search])

  const enabled = debounced.length >= 2
  const path = useMemo(
    () => `/crm?type=${type}&search=${encodeURIComponent(debounced)}`,
    [type, debounced]
  )
  const { data, loading, error, reload } = useAsync<CrmResponse>(
    () => (enabled ? api.get<CrmResponse>(path) : Promise.resolve({ clients: [] })),
    [path, enabled]
  )

  const clients = data?.clients ?? []

  return (
    <div className="flex flex-col">
      <PrivacyBanner />

      <div data-tour="crm-search" className="sticky top-0 z-10 space-y-3 bg-surface px-4 pb-3 pt-4">
        <div className="flex items-center gap-2 rounded-xl border border-line bg-surface-sunken px-3">
          <Search className="h-4 w-4 text-ink-faint" />
          <input
            className="w-full bg-transparent py-2.5 text-sm outline-none placeholder:text-ink-faint"
            placeholder="Buscar cliente…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          {TYPES.map((t) => (
            <button
              key={t.key}
              className={clsx('chip', type === t.key && 'chip-active')}
              onClick={() => setType(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2 p-4">
        {!enabled ? (
          <EmptyState icon={Users} title="Buscá un cliente" subtitle="Por nombre, DNI o email (mínimo 2 caracteres)." />
        ) : loading ? (
          <LoadingScreen label="Buscando…" />
        ) : error ? (
          <ErrorNote message={error.message} onRetry={reload} />
        ) : clients.length === 0 ? (
          <EmptyState icon={ShoppingBag} title="Sin coincidencias" subtitle={`No encontramos clientes para "${debounced}".`} />
        ) : (
          clients.map((c) => <ClientRow key={c.id} client={c} />)
        )}
      </div>
    </div>
  )
}
